import { io, type Socket } from 'socket.io-client';
import { NOTIFICATIONS_WS_NAMESPACE } from '@/types/shared';
import { tokenStorage } from '@/lib/storage';

/**
 * Base URL of the REST API — same env var `src/api/client.ts` reads (`VITE_API_URL`, defaulting to
 * the relative `/api/v1` dev-proxy path, see `.env.example`).
 */
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * The socket.io server shares the SAME HTTP server/port as the REST API but serves its own
 * `/socket.io` transport path, independent of the REST `api/v1` global prefix
 * (`notifications-design.md` §4, D8). So the client connects to the API's plain **origin**
 * (protocol+host+port), not `API_BASE_URL` itself. When `API_BASE_URL` is a relative path (the
 * local-dev proxy convention, e.g. `/api/v1`) there is no origin to parse out of it, so this falls
 * back to the page's own origin — correct both for that proxy setup and for a same-origin
 * production deploy.
 */
function resolveSocketOrigin(): string {
  if (/^https?:\/\//i.test(API_BASE_URL)) {
    return new URL(API_BASE_URL).origin;
  }
  return window.location.origin;
}

/**
 * Creates the notifications socket.io client (T115, DEC-NOT1). Not auto-connected — the caller
 * (`NotificationsContext`) controls the connect/disconnect lifecycle so it can tear the socket down
 * on logout/unmount.
 *
 * `auth` is passed as a FUNCTION, not a static object: socket.io-client invokes it fresh on every
 * connect *and* every reconnect attempt, so a just-refreshed access token (the axios client's
 * refresh-on-401 flow, `src/api/client.ts`) is picked up automatically on the next reconnect rather
 * than the socket being pinned to whatever token existed when it was first constructed.
 */
export function createNotificationsSocket(): Socket {
  return io(`${resolveSocketOrigin()}${NOTIFICATIONS_WS_NAMESPACE}`, {
    autoConnect: false,
    withCredentials: true,
    auth: (callback) => callback({ token: tokenStorage.getAccessToken() }),
  });
}
